import { Col, Row, Spin, Table, Tabs } from "antd";
import { RightOutlined, EditOutlined } from "@ant-design/icons";
import COLORS from "common/Colors";
import KpiHistoryBadge from "./KpiHistoryBadge";
import AdjustDatesModal from "./AdjustDatesModal";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";

const Wrap = styled.div`
  .breadcrumb {
    display: flex;
    align-items: center;
  }
  .perspective-title {
    font-weight: 600;
    margin: 16px 0 8px;
  }
  .ant-table-thead > tr > th {
    background: #C0CDE2 !important;
    font-weight: 600;
    font-size: 13px;
    color: #000000;
  }
  .ant-table-sticky-holder {
    background: #fff;
    z-index: 3;
  }
  .ant-table-tbody > tr > td {
    font-size: 13px;
    padding: 8px 12px;
  }
  .planning-group-row > td {
    background: #fafafa;
  }
`;

const trainingColumns = [
  { title: "S.No", dataIndex: "sno", key: "sno", width: 60 },
  { title: "Planned areas for strengthening", dataIndex: "skill", key: "skill" },
  { title: "Method of development", dataIndex: "method", key: "method" },
  { title: "Planned / Expected quarter", dataIndex: "quarter", key: "quarter", width: 160 },
  {
    title: "Development / training taken (also indicate method & place)",
    dataIndex: "taken",
    key: "taken",
    render: (val) => val || "-",
  },
];

const softSkillsColumns = [
  { title: "Objectives", dataIndex: "objectives", key: "objectives" },
  { title: "Weightage (%)", dataIndex: "weightage", key: "weightage", width: 110 },
];

const formatDate = (value) => (value ? dayjs(value).format("Do MMM YYYY") : "-");

const groupByPerspective = (perspectives = []) => {
  const grouped = {};
  perspectives.forEach((item) => {
    const key = item.perspective || "Other";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(item);
  });
  return grouped;
};

// This page is the appraiser's read-only view of a KPI Planning cycle while
// it's sitting in Appraisal > My Team > Awaiting Appraisee Response - the
// appraisee hasn't attended yet, so there's nothing to score or close, just
// the plan and whichever progress-review checkpoints have completed so far.
const AwaitingAppraiseeResponse = () => {
  const history = useHistory();
  const location = useLocation();
  // Opened from My Team or the HR tab - Back / Done returns there.
  const backTab = location.state?.fromTab || "myteam";
  const appraisalid = location.state?.appraisalid;

  const [appraisal, setAppraisal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("planning");
  const [adjustDatesOpen, setAdjustDatesOpen] = useState(false);

  const fetchAppraisal = async () => {
    setLoading(true);
    try {
      const { data, code } = await CALL_API(
        `performance-kpi-management/appraisal/${appraisalid}`,
        "get"
      );
      if (code === STATUS_CODE.SUCCESS) setAppraisal(data);
    } catch (error) {
      console.error("Error fetching appraisal:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (appraisalid) fetchAppraisal();
  }, [appraisalid]);

  const isReviewTab = activeTab !== "planning";
  const planningColSpan = isReviewTab ? 6 : 5;
  const fullSpanCell = (record) => (record.isGroupHeader ? { colSpan: planningColSpan } : {});
  const hiddenCell = (record) => (record.isGroupHeader ? { colSpan: 0 } : {});

  const planningColumns = [
    {
      title: "S.No",
      key: "sno",
      width: 60,
      onCell: fullSpanCell,
      render: (_, record) =>
        record.isGroupHeader ? (
          <div className="perspective-title m-0">{record.perspective}</div>
        ) : (
          record.sno
        ),
    },
    {
      title: "Measurement Dimensions",
      dataIndex: "measurementdimension",
      key: "measurementdimension",
      onCell: hiddenCell,
    },
    { title: "Objectives", dataIndex: "objectives", key: "objectives", onCell: hiddenCell },
    { title: "Weightage (%)", dataIndex: "weightage", key: "weightage", width: 110, onCell: hiddenCell },
    ...(isReviewTab
      ? [
          {
            title: "Achievements / Progress to date",
            key: "achievements",
            onCell: hiddenCell,
            render: (_, record) => record.progressupdates?.[activeTab]?.achievements || "-",
          },
          {
            title: "Emphasis / Corrective actions",
            key: "correctiveactions",
            onCell: hiddenCell,
            render: (_, record) => record.progressupdates?.[activeTab]?.correctiveactions || "-",
          },
        ]
      : [
          {
            title: "Planning Assumptions / Comments",
            dataIndex: "comments",
            key: "comments",
            onCell: hiddenCell,
            render: (val) => val || "-",
          },
        ]),
  ];

  const rolloverColumns = [
    { title: "S.No", dataIndex: "sno", key: "sno", width: 60 },
    { title: "Objectives", dataIndex: "objectives", key: "objectives" },
    { title: "Weightage (%)", dataIndex: "weightage", key: "weightage", width: 110 },
    ...(isReviewTab
      ? [
          {
            title: "Achievements / Progress to date",
            key: "achievements",
            render: (_, row) => row.progressupdates?.[activeTab]?.achievements || "-",
          },
          {
            title: "Emphasis / Corrective actions",
            key: "correctiveactions",
            render: (_, row) => row.progressupdates?.[activeTab]?.correctiveactions || "-",
          },
        ]
      : [
          {
            title: "Planning Assumptions / Comments",
            dataIndex: "comments",
            key: "comments",
          },
        ]),
  ];

  const buildRows = (grouped) => {
    const rows = [];
    Object.entries(grouped).forEach(([perspective, items]) => {
      rows.push({ key: `group-${perspective}`, isGroupHeader: true, perspective });
      items.forEach((row) => rows.push({ ...row, key: `${perspective}-${row.sno}` }));
    });
    return rows;
  };

  const grouped = groupByPerspective(appraisal?.contractperspectives);
  const rolloverRows = appraisal?.contractrolloverresponsibilities || [];
  const softSkillsRows = appraisal?.contractsoftskillsassessment || [];
  const trainingRows = appraisal?.contracttrainingdevelopmentplan || [];

  const tabItems = [
    { key: "planning", label: "KPI Planning" },
    ...(appraisal?.contractprogressreviewdates || [])
      .filter((item) => item.completed)
      .map((item) => ({ key: item.label, label: `${item.label} Review` })),
  ];

  return (
    <Wrap>
      <Spin spinning={loading}>
        <div className="breadcrumb mb-3">
          <p
            className="lite-text p-0 m-0"
            style={{ cursor: "pointer" }}
            onClick={() => history.push("/performance-kpi", { id: "appraisal", subTab: backTab })}
          >
            Performance Management
          </p>
          &nbsp;
          <RightOutlined className="mt-1" />
          &nbsp;
          <p className="m-0 p-0">{appraisal?.fullname || "-"}</p>
        </div>

        <div className="d-flex align-items-center justify-content-between">
          <h6 className="m-0">Awaiting Appraisee Response</h6>
          {appraisal?.contractid && (
            <span
              style={{ color: COLORS.PRIMARY, cursor: "pointer" }}
              onClick={() => setAdjustDatesOpen(true)}
            >
              <EditOutlined /> Adjust Date
            </span>
          )}
        </div>

        <Row gutter={[16, 16]} className="my-3">
          <Col xs={24} md={8}>
            <div className="lite-text">Name of the employee</div>
            <div className="text-bold">{appraisal?.fullname || "-"}</div>
          </Col>
          <Col xs={24} md={8}>
            <div className="lite-text">Start date to attend appraisal</div>
            <div className="text-bold">{formatDate(appraisal?.startdate)}</div>
          </Col>
          <Col xs={24} md={8}>
            <div className="lite-text">Progress Review</div>
            <div className="text-bold">
              {(appraisal?.contractprogressreviewdates || []).length > 0
                ? appraisal.contractprogressreviewdates.map((item, idx) => (
                    <div key={idx}>
                      {item.label} - {formatDate(item.date)}
                    </div>
                  ))
                : "No"}
            </div>
          </Col>
        </Row>

        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />

        <h6 className="mt-4">Planning</h6>
        <p className="lite-text">
          Objectives or other developmental activities to be undertaken by Appraisee during the
          appraisal period.
        </p>
        <Table
          columns={planningColumns}
          dataSource={buildRows(grouped)}
          pagination={false}
          rowKey={(row) => row.key}
          sticky={{ getContainer: () => document.querySelector(".ant-layout-content") }}
          rowClassName={(record) => (record.isGroupHeader ? "planning-group-row" : "")}
        />

        {rolloverRows.length > 0 && (
          <>
            <div className="perspective-title">Rollover Responsibility from Previous Appraisal</div>
            <Table
              columns={rolloverColumns}
              dataSource={rolloverRows}
              pagination={false}
              rowKey={(row, idx) => `rollover-${idx}`}
            />
          </>
        )}

        {softSkillsRows.length > 0 && (
          <>
            <div className="perspective-title mt-4">Individual (soft skills) Assessment</div>
            <Table
              columns={softSkillsColumns}
              dataSource={softSkillsRows}
              pagination={false}
              rowKey={(row) => `softskills-${row.objectives}`}
            />
          </>
        )}

        {trainingRows.length > 0 && (
          <>
            <div className="perspective-title mt-4">Training & Development Plan</div>
            <p className="lite-text">
              Based on the strengths and limitations identified in the previous review, indicate the
              work experience and/or training suggested during the plan period.
            </p>
            <Table
              columns={trainingColumns}
              dataSource={trainingRows}
              pagination={false}
              rowKey={(row) => `training-${row.sno}`}
            />
          </>
        )}

        <div className="perspective-title mt-4">
          {appraisal?.kpidefinedby === "Self" ? "Appraisee's Comment" : "Appraiser's Comment"}
        </div>
        <p>{appraisal?.appraiseecomment || "-"}</p>

        {appraisal?.contractid && (
          // Sticky (not fixed) so it stays pinned to the bottom of the
          // content area while scrolling without overlapping the sidebar -
          // no background/shadow of its own, just the badge itself floating.
          <div style={{ position: "sticky", bottom: 16, zIndex: 1000 }}>
            <KpiHistoryBadge contractid={appraisal.contractid} />
          </div>
        )}
      </Spin>

      <AdjustDatesModal
        open={adjustDatesOpen}
        onClose={() => setAdjustDatesOpen(false)}
        record={{
          id: appraisal?.contractid,
          startdate: appraisal?.startdate,
          progressreviewdates: appraisal?.contractprogressreviewdates,
        }}
        onSuccess={fetchAppraisal}
      />
    </Wrap>
  );
};

export default AwaitingAppraiseeResponse;
